import { ChatbotApiCaller } from '@frontend/api'

import type { Chatbot } from '@types'
import ChatProvider, { type ChatOptions } from '@ui/chat/ChatProvider'
import { LoadingSpinner } from '@ui/components'
import { useSentry } from '@utils/frontend'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import ChatbotAppMessages from './ChatbotAppChatMessages'

import { authenticate } from '../../utils/auth'
import { getAccessToken, setAccessToken } from '../../utils/storage'
import NotFound from '../errors/NotFound'
import Unauthorized from '../errors/Unauthorized'

export default function ChatbotAppChat() {
	const { id } = useParams()

	const [chat, setChat] = useState<Chatbot | undefined>(undefined)
	const [error, setError] = useState<unknown>(undefined)
	const [isInPreviewMode, setIsInPreviewMode] = useState(false)
	const { captureException } = useSentry()

	useEffect(() => {
		async function getChat() {
			try {
				if (!id) {
					throw new Error('No chatbot id provided')
				}

				const preview = Boolean(new URLSearchParams(window.location.search).get('preview') as string | undefined)

				setIsInPreviewMode(preview)

				let accessToken = getAccessToken(id, preview)

				if (accessToken && !preview) {
					setAccessToken(id, accessToken, preview)
				} else {
					const response = await authenticate(id)

					accessToken = response.accessToken
				}

				const chat = await ChatbotApiCaller.getChatbot(id, accessToken)

				setChat(chat)
			} catch (error: unknown) {
				captureException(error)
				setError(error)
			}
		}

		getChat()
	}, [])

	if ((error as any)?.response?.status === 401) {
		return <Unauthorized />
	}

	if (error) {
		return <NotFound />
	}

	if (!chat) {
		return (<div className='w-full h-full flex justify-center items-center'><LoadingSpinner size='medium' /></div>)
	}

	const chatOptions: ChatOptions = {
		type: 'chatbot',
		displaySources: chat.displaySources,
		displaySuggestedPrompts: chat.displaySuggestedPrompts,
		displayActions: false,
		displayTips: false,
		botIcon: chat.profilePicture?.location,
		color: chat.color
	}

	return (
		<div className='relative flex h-full max-w-full flex-1 flex-col overflow-hidden'>
			<div className='relative top-0 w-full h-12 flex justify-center items-center'>
				<h2
					className='text-xl font-bold leading-6 text-gray-900'
				>
					{ chat.name }
				</h2>
			</div>
			<ChatProvider options={ chatOptions }>
				<ChatbotAppMessages chat={ chat } isInPreviewMode={ isInPreviewMode } />
			</ChatProvider>
		</div>
	)
}
